import React from 'react';
import { Play } from 'lucide-react';
import { useLanguage } from '@/contexts/language/LanguageContext';

// Martin's podcast appearances. Both episodes are in Slovak, so the strip shows on the SK homepage only.
// Newest first; titles without the "Martin Faith (Stratodata):" prefix, the heading already says who it is.
const EPISODES = [
  {
    show: 'Eshopový kráľ',
    title: 'Ako Stratodata učí e-shopy rozumieť svojim dátam a rozhýbať ich pomocou AI',
    thumbnail: 'https://i.ytimg.com/vi/md67ev7d96c/hqdefault.jpg',
    youtube: 'https://www.youtube.com/watch?v=md67ev7d96c',
    spotify: 'https://open.spotify.com/episode/2STRjsnRIb9DgvNR9zDJs7',
  },
  {
    show: 'UPTERDAM',
    title: 'Dátami k profitabilnej expanzii',
    thumbnail: 'https://i.ytimg.com/vi/StXj8udO3xQ/hqdefault.jpg',
    youtube: 'https://www.youtube.com/watch?v=StXj8udO3xQ',
    spotify: '',
  },
];

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#FF0000" d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8Z" />
    <path fill="#fff" d="m9.6 15.6 6.2-3.6-6.2-3.6v7.2Z" />
  </svg>
);

const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#1DB954" />
    <path fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="1.8" d="M6.5 9.3c3.8-1.1 7.9-.8 11.2 1M7.2 12.6c3.1-.8 6.4-.5 9.1 1M7.9 15.7c2.4-.6 4.9-.4 7 .8" />
  </svg>
);

const sourceClass =
  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-gray-200 text-xs font-semibold text-[#2A1E47] hover:border-[#2A1E47]/40 transition-colors';

// Used on the SK homepage (under the hero) and on /sk/about (under the principles). className sets the outer spacing.
const PodcastSection = ({ className = '' }: { className?: string }) => {
  const { language } = useLanguage();
  if (language !== 'sk') return null;

  return (
    <section className={className}>
      <div className="max-w-5xl mx-auto">
        <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-6">
          Martin Faith v podcastoch
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {EPISODES.map((ep) => (
            <div key={ep.youtube} className="flex items-center gap-4 bg-[#FAFAFA] border border-gray-100 rounded-2xl p-3 md:p-4">
              <a
                href={ep.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="relative shrink-0 w-28 md:w-36 aspect-video rounded-lg overflow-hidden group"
                aria-label={`${ep.title} na YouTube`}
              >
                <img src={ep.thumbnail} alt="" loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-[#2A1E47]/30 group-hover:bg-[#2A1E47]/45 transition-colors">
                  <Play className="h-7 w-7 text-white fill-white" />
                </span>
              </a>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#2A1E47]/50 mb-1">{ep.show}</p>
                <p className="text-sm md:text-base font-bold text-[#2A1E47] leading-snug mb-2">{ep.title}</p>
                <div className="flex flex-wrap gap-2">
                  <a href={ep.youtube} target="_blank" rel="noopener noreferrer" className={sourceClass}>
                    <YouTubeIcon />
                    YouTube
                  </a>
                  {ep.spotify && (
                    <a href={ep.spotify} target="_blank" rel="noopener noreferrer" className={sourceClass}>
                      <SpotifyIcon />
                      Spotify
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PodcastSection;
